/**
 * HIS V4 · PresenceClientV4 — ชีวิตของ presence session 1 tab ต่อ 1 หน้า (page_key)
 *
 * วิธีทำงาน (docs/presence-plan.md):
 *   1) join() ตอนเปิดหน้า -> ได้ peers snapshot + cfg จาก backend
 *   2) heartbeat() ตาม cfg.heartbeat_ms (backend ต่อ TTL และ broadcast เฉพาะตอน state เปลี่ยน)
 *   3) รับ delta จาก vc-websocket (ห้อง division เดียวกับที่ MainHIS สมัครไว้) แบบเร็ว
 *   4) REST polling fallback ทุก cfg.poll_ms (10 วินาที) เมื่อ websocket เงียบ
 *      -> ฟีเจอร์ทำงานได้แม้บน localhost ที่ไม่มี vc-websocket server
 *   5) setTyping()/setActive() throttle ตาม cfg.typing_throttle_ms + sync ให้ tab อื่นบนเครื่องเดียวกัน
 *   6) leave() ตอนเปลี่ยนหน้า/ปิด tab (ถ้าไม่ทัน TTL ของ backend จะเก็บกวาดเอง)
 *
 * ทุก timeout อ่านจาก backend เท่านั้น (ค่า DEFAULT_... ใช้ก่อน join สำเร็จเท่านั้น)
 */

import { vcMessenger } from "react-lib/compat/vc-websocket";
import type { WSMessenger, WsMessage } from "react-lib/compat/vc-websocket";

import { presenceApiV4 } from "./presenceApiV4";
import type { PresenceApiV4, PresenceRequest } from "./presenceApiV4";
import {
  DEFAULT_PRESENCE_CONFIG,
  isOtherPeer,
} from "./presenceTypesV4";
import type {
  PresenceChannel,
  PresenceConfig,
  PresencePeer,
  PresenceStateValue,
  PresenceStatus,
} from "./presenceTypesV4";

export const PRESENCE_SOURCE = "presence";
export const PRESENCE_EVENT = "presence_update";

/** หลังหยุดพิมพ์เท่านั้น (วินาที) state จะกลับเป็น ACTIVE */
export const TYPING_IDLE_MS = 2500;
/** ถ้าไม่ได้เห็น message จาก websocket เกิน poll_ms x ตัวเลขนี้ = ถือว่า ws ไม่ทำงาน -> poll เต็มอัตรา */
export const WS_TRUST_WINDOW = 3;

/** API error ต่อเนื่องเกินเกณฑ์นี้ -> ถอยระยะ polling เป็น x(factor) (site เก่า/backend ไม่พร้อม) */
export const FAILURE_BACKOFF_AFTER = 3;
export const FAILURE_BACKOFF_FACTOR = 3;

export type PresenceClientOptions = {
  pageKey: string;
  divisionId: number;
  apiToken?: string | null;
  /** id ต่อ tab (จาก clientIdOf()) — เหมือนกันตลอดทั้ง reload ของ tab นี้ */
  clientId: string;
  /** เรียกทุกครั้งที่ list peer เปลี่ยน (sorted พร้อมแสดงแล้ว) */
  onPeers: (
    peers: PresencePeer[],
    info: { rev: number; self?: PresencePeer; cfg: PresenceConfig; channel: PresenceChannel },
  ) => void;
  onStatus?: (status: PresenceStatus) => void;
  /** ฉีดเข้าได้ตอน test (ปกติใช้ vcMessenger singleton ตัวเดียวกับ MainHIS) */
  messenger?: WSMessenger;
  /** REST layer (test ใส่ fake ได้) — default = presenceApiV4 ของ axios จริง */
  api?: PresenceApiV4;
  /** เปิด/ปิด BroadcastChannel sync ระหว่าง tab บนเครื่องเดียวกัน */
  useLocalBus?: boolean;
  clock?: () => number;
};

type PendingState = { state: PresenceStateValue; field: string | null };

export class PresenceClientV4 {
  private readonly options: PresenceClientOptions;
  private readonly messenger: WSMessenger;
  private readonly clock: () => number;
  private readonly useLocalBus: boolean;
  private readonly api: PresenceApiV4;

  private peers = new Map<string, PresencePeer>();
  private cfg: PresenceConfig = { ...DEFAULT_PRESENCE_CONFIG };
  private rev = 0;
  private selfSid: string | null = null;

  private state: PresenceStateValue = "OPEN";
  private field: string | null = null;
  private sent: PendingState = { state: "OPEN", field: null };

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;

  private started = false;
  private stopped = false;
  private disabledByBackend = false;
  private hidden = false;
  /** เวลา (ms) ที่สุดท้ายแล้วที่มี presence message วิ่งผ่าน websocket */
  private lastWsAt = 0;
  /** จำนวน request ที่ล้มเหลวติดต่อกัน (404/5xx/network) — ใช้ถอยระยะ polling */
  private failures = 0;
  private lastPollAt = 0;
  private lastSentAt = 0;
  private requestInFlight = false;
  private queued: PendingState | null = null;
  private pendingSend: PendingState | null = null;

  private wsMessageHandler = (message: WsMessage): void => this.onWsMessage(message);
  private wsOpenHandler = (): void => this.onWsOpenChange(true);
  private wsCloseHandler = (): void => this.onWsOpenChange(false);
  private visibilityHandler = (): void => this.onVisibilityChange();
  private unloadHandler = (): void => this.onUnload();

  constructor(options: PresenceClientOptions) {
    this.options = options;
    this.messenger = options.messenger ?? vcMessenger;
    this.clock = options.clock ?? (() => Date.now());
    this.useLocalBus = options.useLocalBus !== false;
    this.api = options.api ?? presenceApiV4;
  }

  // ---------------------------------------------------------------- lifecycle

  start(): void {
    if (this.started || this.stopped) return;
    this.started = true;

    this.addWsListeners();
    if (typeof document !== "undefined" && typeof window !== "undefined") {
      document.addEventListener("visibilitychange", this.visibilityHandler);
      window.addEventListener("pagehide", this.unloadHandler);
      window.addEventListener("beforeunload", this.unloadHandler);
    }

    this.hidden = typeof document !== "undefined" && document.visibilityState !== "visible";

    void this.join();
    this.startTimers();
  }

  /** ปิดใช้งานถาวร (unmount / เปลี่ยนหน้า / logout) */
  stop(options: { leaveRoom?: boolean } = {}): void {
    if (this.stopped) return;
    this.stopped = true;

    this.clearTimers();
    this.removeWsListeners();
    if (typeof document !== "undefined" && typeof window !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      window.removeEventListener("pagehide", this.unloadHandler);
      window.removeEventListener("beforeunload", this.unloadHandler);
    }

    if (options.leaveRoom !== false && !this.disabledByBackend) {
      /* unmount ธรรมดา = page กำลังปิด/เปลี่ยนหน้า -> keepalive (เบากว่า) / เปลี่ยนหน้าใน SPA ใช้ leave() ปกติ */
      if (options.leaveRoom === true) void this.api.leave(this.requestOf({}));
      else this.api.leaveKeepalive(this.requestOf({}));
    }
    this.peers.clear();
  }

  // ------------------------------------------------------------- public state

  setActive(): void {
    this.pushState("ACTIVE", null);
  }

  setTyping(field: string): void {
    const name = String(field || "").trim();
    if (!name || name.toLowerCase().endsWith("pass")) return; /* ไม่แจ้ง typing ในช่องรหัสผ่าน */
    this.pushState("TYPING", name);
  }

  currentStatus(): PresenceStatus {
    return {
      channel: this.channelOf(),
      wsConnected: this.isWsConnected(),
      syncedAt: this.lastWsAt,
    };
  }

  /** MainHIS เปลี่ยน apiToken ได้ (token หมดอายุ/refresh) — ใช้อันล่าสุดเสมอโดยไม่ต้อง join ใหม่ */
  updateApiToken(apiToken: string | null): void {
    this.options.apiToken = apiToken;
  }

  // ---------------------------------------------------------------- internals

  private requestOf(extra: { state?: PresenceStateValue; field?: string | null; meta?: Record<string, unknown> }): PresenceRequest {
    return {
      apiToken: this.options.apiToken ?? null,
      divisionId: this.options.divisionId,
      pageKey: this.options.pageKey,
      clientId: this.options.clientId,
      ...extra,
    };
  }

  private async join(): Promise<void> {
    const result = await this.api.join(this.requestOf({ state: "OPEN" }));
    if (this.stopped) return;

    if (result.disabled) {
      this.disabledByBackend = true;
      this.clearTimers();
      this.peers.clear();
      this.emit();
      this.options.onStatus?.({ channel: "off", wsConnected: this.isWsConnected(), syncedAt: this.clock() });
      return;
    }
    if (!result.ok || !result.data) {
      /* join ล้ม (offline/token หมด/backend ไม่พร้อม) -> ลองใหม่ด้วย polling ครั้งถัดไป ไม่ทำให้หน้าพัง */
      this.failures += 1;
      this.options.onStatus?.({
        channel: "poll",
        wsConnected: this.isWsConnected(),
        syncedAt: this.clock(),
        error: result.error,
      });
      return;
    }

    this.cfg = result.data.cfg ?? this.cfg;
    /* sid ของเราต้องรู้ก่อน applySnapshot ไม่งั้น tab ตัวเองจะถูกนับเป็น peer คนอื่น */
    this.selfSid = result.data.self?.sid ?? this.selfSid;
    this.applySnapshot(result.data.peers ?? [], result.data.rev ?? 0);
    this.sent = { state: "OPEN", field: null };
    /* เปิดหน้าครั้งแรก: state change แรกต้องส่งทันที (throttle ใช้กับเฉพาะการพิมพ์รัว ๆ ตามหลัง) */
    this.lastSentAt = 0;
    this.emit();
  }

  private startTimers(): void {
    if (this.stopped || this.disabledByBackend || this.hidden) return;
    this.clearTimers();

    const heartbeatMs = Math.max(this.cfg.heartbeat_ms, 3000);
    const pollMs = Math.max(this.cfg.poll_ms, 2000);

    this.heartbeatTimer = setInterval(() => {
      void this.sendState(this.state, this.field, true);
    }, heartbeatMs);

    /* REST polling fallback: 10 วินาทีตาม backend กำหนด (ใช้เมื่อ websocket เงียบเกิน WS_TRUST_WINDOW) */
    this.pollTimer = setInterval(() => {
      if (this.hidden || this.stopped) return;
      if (this.clock() - this.lastWsAt < pollMs * WS_TRUST_WINDOW) return;
      /* backend ไม่พร้อม/ error ติดต่อกัน -> ลดความถี่ลง (poll_ms x FAILURE_BACKOFF_FACTOR) */
      if (this.failures >= FAILURE_BACKOFF_AFTER && this.clock() - this.lastPollAt < pollMs * FAILURE_BACKOFF_FACTOR) return;
      void this.poll();
    }, pollMs);
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.typingTimer) clearTimeout(this.typingTimer);
    if (this.throttleTimer) clearTimeout(this.throttleTimer);
    this.heartbeatTimer = null;
    this.pollTimer = null;
    this.typingTimer = null;
    this.throttleTimer = null;
    this.pendingSend = null;
  }

  private async poll(): Promise<void> {
    this.lastPollAt = this.clock();
    const result = await this.api.listPeers(this.requestOf({}));
    if (this.stopped) return;
    if (result.disabled) {
      /* endpoint ไม่มีจริง (404/HTML) หรือ constance ปิด -> หยุดทุกอย่างถาวรใน session นี้ */
      this.disabledByBackend = true;
      this.clearTimers();
      this.peers.clear();
      this.emit();
      this.options.onStatus?.({ channel: "off", wsConnected: this.isWsConnected(), syncedAt: this.clock() });
      return;
    }
    if (!result.ok || !result.data) {
      this.failures += 1;
      return;
    }
    this.failures = 0;
    this.applySnapshot(result.data.peers ?? [], result.data.rev ?? 0);
    this.emit();
  }

  /** state ปัจจุบันเปลี่ยน (พิมพ์/กลับเข้ามาในช่อง) — throttle + รวม request ที่ซ้อนกัน */
  private pushState(state: PresenceStateValue, field: string | null): void {
    if (this.stopped || this.disabledByBackend || !this.started) return;

    this.state = state;
    this.field = field;

    if (this.typingTimer) clearTimeout(this.typingTimer);
    if (state === "TYPING") {
      this.typingTimer = setTimeout(() => {
        if (!this.stopped && this.state === "TYPING") this.pushState("ACTIVE", null);
      }, TYPING_IDLE_MS);
    }

    void this.sendState(state, field, false);
  }

  private async sendState(state: PresenceStateValue, field: string | null, force: boolean): Promise<void> {
    if (this.stopped || this.disabledByBackend || this.hidden) return;

    if (this.requestInFlight) {
      /* มี request ค้างอยู่ -> เก็บ state ล่าสุดไว้ส่งต่อเมื่อ request ปัจจุบันจบ */
      this.queued = { state, field };
      return;
    }

    const changed = this.sent.state !== state || (this.sent.field || null) !== (field || null);
    const throttleMs = Math.max(this.cfg.typing_throttle_ms, 300);
    const since = this.clock() - this.lastSentAt;

    if (!changed && !force) return; /* state เดิม: backend/heartbeat จัดการ "ยังอยู่นี่" ให้ */

    if (since < throttleMs) {
      /* พิมพ์รัว ๆ -> เลื่อนไปส่งหลังพ้นช่วง throttle (กันหลุด state เปลี่ยน แต่ไม่ถล่ม API) */
      this.pendingSend = { state, field };
      if (!this.throttleTimer) {
        this.throttleTimer = setTimeout(() => {
          this.throttleTimer = null;
          const next = this.pendingSend;
          this.pendingSend = null;
          if (next) void this.sendState(next.state, next.field, false);
        }, throttleMs - since);
      }
      return;
    }

    this.requestInFlight = true;
    const result = await this.api.heartbeat(this.requestOf({ state, field }));
    this.requestInFlight = false;

    if (this.stopped) return;
    if (result.disabled) {
      this.disabledByBackend = true;
      this.clearTimers();
      this.peers.clear();
      this.emit();
      return;
    }
    if (result.data?.cfg) this.cfg = result.data.cfg;

    if (!result.ok) this.failures += 1;

    if (result.ok) {
      this.failures = 0;
      this.sent = { state, field };
      this.lastSentAt = this.clock();
      this.syncSelfPeer(state, field);
      if (this.useLocalBus) this.broadcastLocalPeer(state, field);
    }

    if (this.queued) {
      const next = this.queued;
      this.queued = null;
      void this.sendState(next.state, next.field, false);
    }
  }

  private syncSelfPeer(state: PresenceStateValue, field: string | null): void {
    if (!this.selfSid) return;
    const existing = this.peers.get(this.selfSid);
    if (!existing) return;
    const nowSec = Math.floor(this.clock() / 1000);
    this.peers.set(this.selfSid, {
      ...existing,
      state,
      field: field ?? null,
      last_seen: nowSec,
      seconds_open: Math.max(0, nowSec - (existing.opened_at || nowSec)),
      self: true,
      own: true,
    });
    this.emit();
  }

  /** tab อื่นบนเครื่องเดียวกัน (BroadcastChannel) ได้เห็นทันที ไม่ต้องรอ backend */
  private broadcastLocalPeer(state: PresenceStateValue, field: string | null): void {
    const peer = this.selfSid ? this.peers.get(this.selfSid) : undefined;
    if (!peer) return;
    try {
      this.messenger.send_local_message(
        JSON.stringify({
          source: PRESENCE_SOURCE,
          event: PRESENCE_EVENT,
          page_key: this.options.pageKey,
          rev: this.rev,
          peers: [{ ...peer, self: false }],
          removed: [],
          reason: "local",
        }),
      );
    } catch (error) {
      /* BroadcastChannel ไม่รองรับ (Safari บางเวอร์ชัน) -> มี ws/poll อยู่แล้ว */
    }
  }

  // ------------------------------------------------------------- websocket in

  private addWsListeners(): void {
    try {
      this.messenger.onMessage(this.wsMessageHandler);
      this.messenger.onConnectionOpen(this.wsOpenHandler);
      this.messenger.onConnectionClose(this.wsCloseHandler);
    } catch (error) {
      /* messenger ไม่พร้อม (หน้า mobile/QML) -> polling อย่างเดียว */
    }
  }

  private removeWsListeners(): void {
    try {
      this.messenger.removeOnMessage(this.wsMessageHandler);
      this.messenger.removeOnConnectionOpen(this.wsOpenHandler);
      this.messenger.removeOnConnectionClose(this.wsCloseHandler);
    } catch (error) {
      /* ignore */
    }
  }

  private isWsConnected(): boolean {
    try {
      return this.messenger.isConnected();
    } catch (error) {
      return false;
    }
  }

  private onWsOpenChange(connected: boolean): void {
    if (this.stopped || this.disabledByBackend) return;
    /* พึ่งเชื่อมต่อใหม่: ดึง snapshot ทันทีกันแถบแสดงค่าเก่า */
    if (connected) void this.poll();
    this.options.onStatus?.({ channel: this.channelOf(), wsConnected: connected, syncedAt: this.clock() });
  }

  private onWsMessage(message: WsMessage): void {
    if (!message || typeof message !== "object") return;
    /* ปิดอยู่ (backend ไม่มี feature / constance ปิด) -> อย่าให้ delta ปลุกแถบขึ้นมาใหม่ */
    if (this.stopped || this.disabledByBackend) return;
    if (message.source !== PRESENCE_SOURCE || message.event !== PRESENCE_EVENT) return;
    if (message.page_key !== this.options.pageKey) return;

    this.lastWsAt = this.clock();
    const peers = (message.peers ?? []) as PresencePeer[];
    const removed = (message.removed ?? []) as string[];
    const rev = Number(message.rev ?? 0);

    if (peers.length) {
      peers.forEach((peer) => {
        if (!peer || !peer.sid) return;
        if (peer.sid === this.selfSid) this.selfSid = peer.sid;
        this.peers.set(peer.sid, { ...peer, self: peer.sid === this.selfSid });
      });
    }
    removed.forEach((sid) => this.peers.delete(sid));

    if (rev > this.rev) this.rev = rev;
    /* delta ที่มาก่อน join ตอบกลับ ยังแยก tab ตัวเองไม่ได้ -> รอ snapshot ของ join */
    if (!this.selfSid) return;
    this.emit();
  }

  private applySnapshot(peers: PresencePeer[], rev: number): void {
    this.peers = new Map(
      peers
        .filter((peer) => peer && peer.sid)
        .map((peer) => [peer.sid, { ...peer, self: peer.sid === this.selfSid }]),
    );
    if (rev > this.rev) this.rev = rev;
  }

  // -------------------------------------------------------------------- misc

  private onVisibilityChange(): void {
    if (this.stopped || this.disabledByBackend) return;
    const visible = typeof document === "undefined" || document.visibilityState === "visible";
    this.hidden = !visible;
    if (visible) {
      this.startTimers();
      void this.poll();
    } else {
      /* tab ซ่อน: หยุดส่ง ออก heartbeat -> backend จะลบ session เมื่อ TTL ครบ */
      this.clearTimers();
    }
  }

  private onUnload(): void {
    if (this.stopped || this.disabledByBackend) return;
    this.api.leaveKeepalive(this.requestOf({}));
    this.stopped = true;
    this.clearTimers();
  }

  private channelOf(): PresenceChannel {
    if (this.disabledByBackend) return "off";
    const pollMs = Math.max(this.cfg.poll_ms, 2000);
    return this.clock() - this.lastWsAt < pollMs * WS_TRUST_WINDOW ? "ws" : "poll";
  }

  private emit(): void {
    const rows = Array.from(this.peers.values())
      .filter(isOtherPeer)
      .sort((a, b) => rankOf(b) - rankOf(a) || String(a.name).localeCompare(String(b.name), "th"));

    this.options.onPeers(rows, {
      rev: this.rev,
      self: this.selfSid ? this.peers.get(this.selfSid) : undefined,
      cfg: this.cfg,
      channel: this.channelOf(),
    });
  }
}

/** กำลังพิมพ์ > กำลังกรอก > เปิดทิ้งไว้ */
const rankOf = (peer: PresencePeer): number =>
  peer.state === "TYPING" ? 3 : peer.state === "ACTIVE" ? 2 : 1;
